import {
  Alert,
  Box,
  FormControlLabel,
  RadioGroup,
} from "@mui/material";
import React from "react";
import { COLORS } from "../assets/theme";
import {
  BulkMnpHlrRequest,
  BulkMnpHlrRequestType,
} from "../services/RoutingPlanDetailsService";
import AutoComplete from "./common/AutoComplete";
import CustomLabel from "./common/CustomLabel";
import CustomRadio from "./CustomRadio";
import { Option } from "../types/table/interfaces.type";

interface BulkMnpHlrContentProps {
  setRequest: React.Dispatch<React.SetStateAction<Partial<BulkMnpHlrRequest>>>;
  request: Partial<BulkMnpHlrRequest>;
  mnpServiceProviderOptions: Option[];
  hlrServiceProviderOptions: Option[];
}

const BulkMnpHlrContent: React.FC<BulkMnpHlrContentProps> = ({
  request,
  setRequest,
  mnpServiceProviderOptions,
  hlrServiceProviderOptions,
}) => {
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequest((prev) => ({
      ...prev,
      type: event.target.value as BulkMnpHlrRequestType,
      mnpServiceProviderId: undefined,
      hlrServiceProviderId: undefined,
    }));
  };

  const showMnp =
    request.type === BulkMnpHlrRequestType.MNP ||
    request.type === BulkMnpHlrRequestType.MNP_HLR;

  const showHlr =
    request.type === BulkMnpHlrRequestType.HLR ||
    request.type === BulkMnpHlrRequestType.MNP_HLR;

  return (
    <Box
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: { xs: 600, md: 800 },
        margin: "0 auto",
      }}
    >
      <RadioGroup
        row
        value={request.type}
        onChange={handleTypeChange}
        sx={{
          width: { xs: "100%", md: "60%" },
          justifyContent: "space-around",
          mb: 4,
        }}
      >
        <FormControlLabel
          value={BulkMnpHlrRequestType.MNP}
          control={<CustomRadio name="bulkMnpHlr" />}
          label="MNP"
        />
        <FormControlLabel
          value={BulkMnpHlrRequestType.HLR}
          control={<CustomRadio name="bulkMnpHlr" />}
          label="HLR"
        />
        <FormControlLabel
          value={BulkMnpHlrRequestType.MNP_HLR}
          control={<CustomRadio name="bulkMnpHlr" />}
          label="MNP / HLR"
        />
      </RadioGroup>

      <Box display="flex" flexDirection="column" gap={3} width="100%">
        {showMnp && (
          <Box>
            <CustomLabel
              text="MNP Service Provider"
              fontSize="14px"
              fontWeight={500}
              color={COLORS.typographyColor}
            />
            <AutoComplete
              label=""
              value={
                request.mnpServiceProviderId == null
                  ? ""
                  : String(request.mnpServiceProviderId)
              }
              options={mnpServiceProviderOptions.map((o) => ({
                label: o.label,
                value: String(o.value),
              }))}
              onChange={(value) => {
                setRequest((prev) => ({
                  ...prev,
                  mnpServiceProviderId: value ? Number(value) : null,
                }));
              }}
              clearable={true}
              disablePortal={false}
              placeholder="Select MNP Service Provider"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.WhiteBackground,
                },
              }}
            />
            {request.mnpServiceProviderId == null && (
              <Alert severity="warning" sx={{ mt: 1, width: "100%" }}>
                Saving without a selection will unassign the MNP service
                provider from the selected routing plans.
              </Alert>
            )}
          </Box>
        )}

        {showHlr && (
          <Box>
            <CustomLabel
              text="HLR Service Provider"
              fontSize="14px"
              fontWeight={500}
              color={COLORS.typographyColor}
            />
            <AutoComplete
              label=""
              value={
                request.hlrServiceProviderId == null
                  ? ""
                  : String(request.hlrServiceProviderId)
              }
              options={hlrServiceProviderOptions.map((o) => ({
                label: o.label,
                value: String(o.value),
              }))}
              onChange={(value) => {
                setRequest((prev) => ({
                  ...prev,
                  hlrServiceProviderId: value ? Number(value) : null,
                }));
              }}
              clearable={true}
              disablePortal={false}
              placeholder="Select HLR Service Provider"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.WhiteBackground,
                },
              }}
            />
            {request.hlrServiceProviderId == null && (
              <Alert severity="warning" sx={{ mt: 1, width: "100%" }}>
                Saving without a selection will unassign the HLR service
                provider from the selected routing plans.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BulkMnpHlrContent;
